import {
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemsGqlQuery
} from '../../../../types';
import {
  FetchSearchFiltersCacheKey,
  FetchSearchFiltersFilters,
  FetchSearchFiltersGqlQuery,
  FetchSearchFiltersLimit,
  FetchSearchFiltersPage,
  FetchSearchFiltersSort
} from '../../searchFiltersTypes';

import { useCacheIndexQuery } from '../../../common/reactQuery/hooks/useCacheIndexQuery';

import {
  INITIAL_SEARCH_FILTERS_FILTERS,
  INITIAL_SEARCH_FILTERS_LIMIT,
  INITIAL_SEARCH_FILTERS_PAGE,
  INITIAL_SEARCH_FILTERS_SORT
} from '../../searchFiltersConstants';

interface SearchFiltersDefaultOptions {
  action?: string;
  cacheKey: FetchSearchFiltersCacheKey;
  query: FetchSearchFiltersGqlQuery;
  initialFilters?: FetchSearchFiltersFilters;
  initialSort?: FetchSearchFiltersSort;
  initialPage?: FetchSearchFiltersPage;
  initialLimit?: FetchSearchFiltersLimit;
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    keepPreviousData?: boolean;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
    withoutPrefetch?: boolean;
  };
}

interface SearchFiltersWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
}

interface SearchFiltersWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
}

type SearchFiltersOptions = SearchFiltersDefaultOptions &
  (
    | SearchFiltersWithPrefetchItemOptions
    | SearchFiltersWithoutPrefetchItemOptions
  );

const scope = 'searchFilters';

function useCacheSearchFilters<ItemType>({
  action,
  cacheKey,
  query,
  initialFilters = INITIAL_SEARCH_FILTERS_FILTERS,
  initialSort = INITIAL_SEARCH_FILTERS_SORT,
  initialPage = INITIAL_SEARCH_FILTERS_PAGE,
  initialLimit = INITIAL_SEARCH_FILTERS_LIMIT,
  fetchItemCacheKey,
  fetchItemQuery,
  source = FetchCacheItemsSources.DB,
  trackTotalHits,
  options = {}
}: SearchFiltersOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    clearItemsFilters,
    filterItems,
    changeItemsFilters,
    prefetchItems,
    sortItems,
    paginateItems,
    limitItems,
    prefetchItem
  } = useCacheIndexQuery<ItemType>({
    action,
    cacheKey,
    cacheQuery: query,
    defaultApi: 'cache_api',
    scope,
    query,
    initialFilters,
    initialLimit,
    initialPage,
    initialSort,
    fetchItemCacheKey,
    fetchItemQuery,
    options,
    source,
    trackTotalHits
  });

  return {
    searchFiltersData: data,
    searchFilters: items,
    searchFiltersError: itemsError,
    searchFiltersTotalCount: itemsTotalCount,
    searchFiltersFetched: isFetched,
    searchFiltersLoading: isLoading,
    searchFiltersIsPlaceholderData: isPlaceholderData,
    searchFiltersFilters: currentFilters,
    searchFiltersSort: currentSort,
    searchFiltersPage: currentPage,
    searchFiltersLimit: currentLimit,
    clearSearchFiltersFilters: clearItemsFilters,
    filterSearchFilters: filterItems,
    changeSearchFiltersFilters: changeItemsFilters,
    limitSearchFilters: limitItems,
    paginateSearchFilters: paginateItems,
    prefetchSearchFilter: prefetchItem,
    prefetchSearchFilters: prefetchItems,
    sortSearchFilters: sortItems
  };
}

export default useCacheSearchFilters;
